import { ArrowUpRight, Bus, Clock } from "lucide-react";
import { useTranslation } from "../../i18n/LanguageContext";

export default function FeaturedTrips({ onQuoteClick }) {
  const { t } = useTranslation();
  const tr = t.trips;

  return (
    <section
      id="kirandulasok"
      data-testid="featured-trips-section"
      className="relative py-16 md:py-32 bg-[#ebe2f5]"
    >
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-16 md:h-24 bg-gradient-to-b from-transparent to-[#dfd3ee]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-20 mb-14 md:mb-24 items-end">
          <div>
            <p className="text-eyebrow">{tr.eyebrow}</p>
            <h2
              data-testid="featured-trips-title"
              className="mt-5 font-serif-display text-[34px] leading-[1.1] sm:text-4xl md:text-5xl lg:text-6xl text-[#0a0a0a] sm:leading-[1.04]"
            >
              {tr.titleA}{" "}
              <span className="italic gold-text">{tr.titleB}</span>
            </h2>
          </div>
          <p className="text-[#8a7f8f] leading-relaxed">{tr.intro}</p>
        </div>

        <div className="mb-14 md:mb-28">
          <div className="flex items-baseline justify-between mb-8 md:mb-14 pb-4 md:pb-5 border-b border-white/10">
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#1a1a1a]">
              {tr.cityHeader}
            </h3>
            <span className="text-eyebrow hidden sm:inline">
              {tr.cityCount}
            </span>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-y-2 md:gap-x-12">
            {tr.cityTours.map((tour, i) => (
              <li
                key={tour.id}
                data-testid={`city-tour-${tour.id}`}
                className="group"
              >
                <button
                  onClick={onQuoteClick}
                  className="w-full text-left flex items-center gap-3 sm:gap-5 md:gap-7 py-5 md:py-7 border-b border-white/[0.06] hover:border-[#0a0a0a]/30 transition-colors"
                >
                  <span className="text-[10px] sm:text-xs tracking-[0.18em] sm:tracking-[0.22em] uppercase text-[#8a7f8f] group-hover:text-[#0a0a0a] transition-colors w-7 sm:w-10 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-xl sm:text-2xl md:text-3xl text-[#0a0a0a] group-hover:text-[#1a1a1a] transition-colors leading-tight">
                      {tour.name}
                    </div>
                    <div className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-[#8a7f8f] line-clamp-1">
                      {tour.subtitle}
                    </div>
                  </div>
                  <span className="hidden md:flex items-center gap-2 text-xs text-[#8a7f8f]">
                    <Clock size={12} />
                    {tr.durationOne}
                  </span>
                  <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-white/10 flex items-center justify-center text-[#594f5a] group-hover:border-[#0a0a0a] group-hover:text-[#0a0a0a] group-hover:rotate-[-45deg] transition-all duration-500 shrink-0">
                    <ArrowUpRight size={14} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="mb-12 md:mb-20">
          <div className="flex items-baseline justify-between mb-8 md:mb-14 pb-4 md:pb-5 border-b border-white/10">
            <h3 className="font-serif-display text-2xl md:text-3xl text-[#1a1a1a]">
              {tr.specialHeader}
            </h3>
            <span className="text-eyebrow hidden sm:inline">
              {tr.specialCount}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7">
            {tr.specials.map((p) => (
              <article
                key={p.id}
                data-testid={`special-program-${p.id}`}
                className="trip-card group relative rounded-3xl overflow-hidden bg-[#f5effb] aspect-[4/5] md:aspect-[5/6]"
              >
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="trip-image absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2a162f]/95 via-[#2a162f]/55 to-[#2a162f]/10" />

                <div className="absolute top-4 left-4 sm:top-5 sm:left-5">
                  <span className="text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 rounded-full glass text-[#1a1a1a]">
                    {p.tag}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-7 md:p-10">
                  <h4 className="font-serif-display text-[28px] sm:text-3xl md:text-5xl text-white sm:leading-[1.05]">
                    {p.name}
                  </h4>
                  <p className="mt-2.5 sm:mt-3 text-[#e0c485] text-sm tracking-wide">
                    {p.subtitle}
                  </p>
                  <p className="mt-4 sm:mt-5 text-white/75 text-sm leading-relaxed max-w-md">
                    {p.description}
                  </p>
                  <button
                    onClick={onQuoteClick}
                    data-testid={`special-program-cta-${p.id}`}
                    className="mt-5 sm:mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white hover:text-[#c5a059] transition-colors group/btn"
                  >
                    {tr.specialCta}
                    <ArrowUpRight
                      size={14}
                      className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform"
                    />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div
          data-testid="transfer-banner"
          className="glass-strong rounded-2xl p-6 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-5"
        >
          <div className="flex items-start md:items-center gap-4">
            <span className="w-12 h-12 rounded-2xl border border-[#0a0a0a]/30 flex items-center justify-center shrink-0">
              <Bus size={18} className="text-[#0a0a0a]" strokeWidth={1.4} />
            </span>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#0a0a0a]">
                {tr.transferBannerEyebrow}
              </div>
              <p className="mt-1 text-[#1a1a1a] text-base md:text-lg leading-snug">
                {tr.transferBannerText}
              </p>
            </div>
          </div>
          <a
            href="#transzfer"
            data-testid="transfer-link"
            className="btn-outline-cream px-5 py-3 rounded-full text-sm inline-flex items-center gap-2 self-start md:self-auto whitespace-nowrap"
          >
            {tr.transferBannerCta}
            <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
